
export default function ArticlePartThree() {
  return (
    <>
      {/* Surviving Spouse Section */}
      <section id="surviving-spouse" className="mb-12 scroll-mt-24">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Surviving Spouse Exemption: Continuing Your Veteran&apos;s Property Tax Benefits
          </h2>

          <div className="mb-10">
            <img
              src="https://public.readdy.ai/ai/img_res/0e78a95b72dd92b430eba63bd47d8662.jpg"
              alt="Surviving spouse of disabled veteran eligible for continued property tax exemption in El Paso Texas"
              className="w-full h-80 object-cover object-top rounded-xl shadow-lg mb-6"
            />
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              Texas law provides important property tax protections for the surviving spouses of disabled veterans. If your spouse was a disabled veteran who qualified for the property tax exemption at the time of their death, you may be eligible to continue receiving the same exemption on your homestead. The specific rules depend on which exemption your spouse held and whether you continue to occupy the same property. Understanding these provisions is essential for military families in El Paso who want to preserve the financial benefits their veteran earned through service.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              For surviving spouses of veterans who held the 100 percent disabled veteran property tax exemption under Section 11.131, Texas Tax Code Section 11.131(b) allows the surviving spouse to continue receiving the total exemption on the same property as long as they do not remarry and continue to occupy the home as their primary residence. This means the surviving spouse continues to pay zero property taxes on the homestead. If the surviving spouse later moves to a different property in Texas, they may transfer the exemption to the new homestead, but the exemption amount is capped at the dollar value of the exemption on the original property. This transfer provision ensures that surviving spouses are not forced to remain in a home that no longer meets their needs simply to preserve their tax benefit.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              For surviving spouses of veterans who held the partial exemption under Section 11.22, the rules are slightly different. The surviving spouse can continue to receive the same partial exemption amount that the veteran was receiving, provided they do not remarry and continue to reside in the homestead. If the veteran had a $12,000 exemption based on a 70 to 100 percent rating, the surviving spouse continues to receive the $12,000 exemption. This benefit transfers to a new homestead if the surviving spouse moves within Texas.
            </p>

            <div className="bg-teal-50 rounded-xl p-8 mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Surviving Spouse Eligibility Requirements
              </h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3"></i>
                  <span className="text-gray-700">
                    Your deceased spouse was receiving the disabled veteran exemption at the time of death
                  </span>
                </div>
                <div className="flex items-center">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3"></i>
                  <span className="text-gray-700">You have not remarried since your spouse&apos;s death</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3"></i>
                  <span className="text-gray-700">
                    You continue to occupy the property as your primary residence (or transfer to a new homestead)
                  </span>
                </div>
                <div className="flex items-center">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3"></i>
                  <span className="text-gray-700">You file the surviving spouse exemption application with EPCAD</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-xl mr-3"></i>
                  <span className="text-gray-700">
                    You provide a copy of the veteran&apos;s death certificate and VA documentation
                  </span>
                </div>
              </div>
            </div>

            <p className="text-gray-700 leading-relaxed mb-6">
              There is also a separate and distinct exemption for surviving spouses of military members who were killed in action or who died in the line of duty. Under Texas Tax Code Section 11.133, the surviving spouse of a member of the armed forces who was killed or fatally injured in the line of duty receives a total property tax exemption on their homestead, regardless of whether the deceased service member had any prior disability rating. This exemption is equivalent to the 100 percent disabled veteran exemption and eliminates all property taxes on the surviving spouse&apos;s homestead. Given the significant military presence at{' '}
              <a
                href="/elpaso-tx/fort-bliss-military-relocation"
                className="text-teal-600 hover:text-teal-700 font-semibold"
              >
                Fort Bliss and the El Paso military community
              </a>
              , this provision is particularly relevant for Gold Star families in our area.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              If you are a surviving spouse who needs to transfer the property into your name through the{' '}
              <a
                href="/elpaso-tx/texas-probate-guide"
                className="text-teal-600 hover:text-teal-700 font-semibold"
              >
                Texas probate process
              </a>{' '}
              or through an{' '}
              <a
                href="/elpaso-tx/affidavit-of-heirship"
                className="text-teal-600 hover:text-teal-700 font-semibold"
              >
                affidavit of heirship
              </a>
              , you should file for the surviving spouse exemption simultaneously. EPCAD can process both the ownership transfer documentation and the exemption application together, which prevents any gap in your tax-exempt status. If you are dealing with{' '}
              <a
                href="/elpaso-tx/texas-community-property-laws"
                className="text-teal-600 hover:text-teal-700 font-semibold"
              >
                Texas community property laws affecting your inherited home
              </a>
              , consult with an estate attorney who understands both military benefits and Texas property law.
            </p>
            <p className="text-gray-700 leading-relaxed">
              The application process for surviving spouses follows the same general steps as the disabled veteran application. You will use Form 50-135 and check the surviving spouse section. In addition to the standard documents, you will need to provide a certified copy of the veteran&apos;s death certificate and documentation showing that the veteran was receiving the disabled veteran exemption at the time of death. EPCAD staff are trained to handle these sensitive situations with compassion and can guide you through the process if you visit their office in person.
            </p>
          </div>
        </div>
      </section>

      {/* Comparison with Other Counties */}
      <section id="county-comparison" className="mb-12 scroll-mt-24">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            El Paso vs. Harris County and Other Texas Counties: How Exemptions Compare
          </h2>

          <div className="mb-10">
            <img
              src="https://readdy.ai/api/search-image?query=panoramic%20texas%20state%20map%20showing%20major%20cities%20el%20paso%20houston%20san%20antonio%20dallas%20with%20property%20markers%20and%20tax%20district%20boundaries%20warm%20toned%20professional%20cartography%20illustration%20style%20clean%20modern%20design&width=800&height=500&seq=dv-counties-807&orientation=landscape"
              alt="Texas map comparing disabled veteran property tax exemptions across El Paso Harris County and other counties"
              className="w-full h-80 object-cover object-top rounded-xl shadow-lg mb-6"
            />
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              One of the most common questions veterans ask when comparing locations is how the disabled veteran property tax exemption works in different Texas counties. The good news is that the exemption amounts under Sections 11.22 and 11.131 are set by state law and apply uniformly across all 254 Texas counties. Whether you live in El Paso, Harris County, Bexar County, Dallas County, or any other jurisdiction, the exemption dollar amounts and eligibility requirements are identical. A veteran with a 50 percent rating receives a $10,000 exemption in El Paso just as they would in Houston. A 100 percent disabled veteran pays zero property taxes in El Paso just as they would in San Antonio.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              However, the actual dollar savings differ significantly between counties because property tax rates vary. The Harris County disabled veteran property tax exemption provides the same exemption amounts, but because Harris County&apos;s combined tax rate is different from El Paso&apos;s, the annual savings in dollars will differ. El Paso&apos;s combined rate of approximately 2.69 percent is higher than many Texas counties, which means the disabled veteran exemption actually saves El Paso veterans more money in absolute terms than veterans in lower-tax areas. This is one of the few situations where El Paso&apos;s{' '}
              <a
                href="/elpaso-tx/why-property-taxes-high-el-paso"
                className="text-teal-600 hover:text-teal-700 font-semibold"
              >
                high property tax rates
              </a>{' '}
              work in a veteran&apos;s favor, because the exemption removes value that would otherwise be taxed at a higher rate.
            </p>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
              <div className="bg-teal-600 text-white p-6">
                <h3 className="text-xl font-bold">
                  100% Disabled Veteran Savings by Texas County (Approximate)
                </h3>
                <p className="text-teal-100 mt-2">
                  Based on $250,000 home value and approximate combined tax rates
                </p>
              </div>
              <div className="p-6">
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b-2 border-gray-200">
                        <th className="py-3 px-4 text-gray-900 font-bold">County</th>
                        <th className="py-3 px-4 text-gray-900 font-bold">Approx. Tax Rate</th>
                        <th className="py-3 px-4 text-gray-900 font-bold">Normal Tax Bill</th>
                        <th className="py-3 px-4 text-gray-900 font-bold">With 100% Exemption</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100 bg-teal-50">
                        <td className="py-3 px-4 text-gray-900 font-semibold">El Paso County</td>
                        <td className="py-3 px-4 text-gray-700">2.69%</td>
                        <td className="py-3 px-4 text-gray-700">$6,725</td>
                        <td className="py-3 px-4 text-teal-600 font-bold">$0 (Save $6,725)</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4 text-gray-700">Harris County (Houston)</td>
                        <td className="py-3 px-4 text-gray-700">2.31%</td>
                        <td className="py-3 px-4 text-gray-700">$5,775</td>
                        <td className="py-3 px-4 text-teal-600 font-bold">$0 (Save $5,775)</td>
                      </tr>
                      <tr className="border-b border-gray-100 bg-gray-50">
                        <td className="py-3 px-4 text-gray-700">Bexar County (San Antonio)</td>
                        <td className="py-3 px-4 text-gray-700">2.45%</td>
                        <td className="py-3 px-4 text-gray-700">$6,125</td>
                        <td className="py-3 px-4 text-teal-600 font-bold">$0 (Save $6,125)</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4 text-gray-700">Dallas County</td>
                        <td className="py-3 px-4 text-gray-700">2.18%</td>
                        <td className="py-3 px-4 text-gray-700">$5,450</td>
                        <td className="py-3 px-4 text-teal-600 font-bold">$0 (Save $5,450)</td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="py-3 px-4 text-gray-700">Tarrant County (Fort Worth)</td>
                        <td className="py-3 px-4 text-gray-700">2.37%</td>
                        <td className="py-3 px-4 text-gray-700">$5,925</td>
                        <td className="py-3 px-4 text-teal-600 font-bold">$0 (Save $5,925)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <p className="text-gray-700 leading-relaxed mb-6">
              As the table shows, El Paso veterans with the 100 percent disabled veteran property tax exemption save more per year than veterans in most other major Texas counties simply because El Paso&apos;s tax rate is higher. A 100 percent disabled veteran in El Paso with a $250,000 home saves $6,725 per year compared to $5,775 in Harris County. Over a decade, that difference amounts to nearly $9,500 more in savings for the El Paso veteran. This is an important consideration for military retirees choosing where to settle after leaving active duty at Fort Bliss.
            </p>
            <p className="text-gray-700 leading-relaxed">
              The application process is also consistent across counties. Whether you are filing with EPCAD in El Paso or the Harris County Appraisal District in Houston, you use the same state forms and provide the same documentation. The only difference is where you submit your application. If you are{' '}
              <a
                href="/elpaso-tx/selling-home-during-pcs"
                className="text-teal-600 hover:text-teal-700 font-semibold"
              >
                relocating from El Paso during a PCS move
              </a>{' '}
              to another Texas location, you will need to file a new exemption application with the appraisal district in your new county. Your exemption does not automatically transfer between counties, so make this a priority item on your moving checklist.
            </p>
          </div>
        </div>
      </section>

      {/* Common Mistakes Section */}
      <section id="mistakes" className="mb-12 scroll-mt-24">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Common Mistakes Veterans Make When Applying for the Property Tax Exemption
          </h2>

          <div className="mb-10">
            <img
              src="https://readdy.ai/api/search-image?query=frustrated%20veteran%20man%20at%20desk%20with%20rejected%20application%20papers%20crumpled%20documents%20and%20laptop%20showing%20error%20message%20home%20office%20setting%20warm%20afternoon%20light%20el%20paso%20texas%20showing%20determination%20to%20fix%20mistake%20professional%20photography&width=800&height=500&seq=dv-mistakes-808&orientation=landscape"
              alt="Veteran reviewing rejected property tax exemption application to correct common mistakes"
              className="w-full h-80 object-cover object-top rounded-xl shadow-lg mb-6"
            />
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              After years of helping veterans navigate the property tax system in El Paso, we have identified the most common mistakes that delay or prevent approval of disabled veteran property tax exemption applications. Avoiding these pitfalls will save you time and ensure you start receiving your benefits as quickly as possible.
            </p>

            <div className="space-y-6 mb-8">
              <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-red-400">
                <h4 className="font-bold text-gray-900 text-lg mb-2">
                  Mistake 1: Using an Outdated VA Rating Letter
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  Many veterans submit a VA letter that is several years old and does not reflect their current rating. If your rating has increased since the letter was issued, you are leaving money on the table. Always download a fresh Benefits Summary Letter from VA.gov before applying. EPCAD needs to see your current rating to apply the correct exemption amount.
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-red-400">
                <h4 className="font-bold text-gray-900 text-lg mb-2">
                  Mistake 2: Not Applying for the 100% Exemption When Eligible for TDIU
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  Veterans who receive Total Disability based on Individual Unemployability often do not realize they qualify for the complete property tax exemption. They apply for the $12,000 partial exemption instead of the total exemption under Section 11.131. If your VA letter indicates TDIU or unemployability, you qualify for zero property taxes. Check your letter carefully and apply for the correct exemption.
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-red-400">
                <h4 className="font-bold text-gray-900 text-lg mb-2">
                  Mistake 3: Failing to Apply for Both Homestead and Veteran Exemptions
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  Some veterans apply for the disabled veteran exemption but forget to also apply for the standard residential homestead exemption. For veterans with partial disability ratings, stacking both exemptions provides significantly greater savings than either one alone. File both applications simultaneously to maximize your tax reduction.
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-red-400">
                <h4 className="font-bold text-gray-900 text-lg mb-2">
                  Mistake 4: Missing the April 30th Deadline
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  The standard deadline for filing exemption applications with EPCAD is April 30th of the tax year. While late applications are accepted and can be filed up to one year after the deadline, filing on time ensures your exemption is applied to the current tax year without any complications. Set a calendar reminder for January to begin the process.
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-red-400">
                <h4 className="font-bold text-gray-900 text-lg mb-2">
                  Mistake 5: Not Updating EPCAD After a Rating Increase
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  If your VA disability rating increases after you have already filed for the exemption, you must notify EPCAD and submit updated documentation. Your exemption does not automatically adjust when your rating changes. A veteran who goes from 60 percent to 80 percent, for example, should update their application to receive the higher $12,000 exemption instead of the $10,000 exemption.
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-red-400">
                <h4 className="font-bold text-gray-900 text-lg mb-2">
                  Mistake 6: Assuming the Exemption Transfers Automatically When Moving
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  If you sell your current home and purchase a new one in El Paso or anywhere in Texas, you must file a new exemption application for the new property. The exemption is tied to a specific property account, not to you personally. Many veterans who move within El Paso go months or even years without the exemption on their new home because they assumed it would follow them automatically.
                </p>
              </div>
            </div>

            <p className="text-gray-700 leading-relaxed">
              If you have already been paying property taxes that you should not have been paying because you did not know about the disabled veteran exemption, you may be able to recover some of those overpayments. Texas law allows you to apply retroactively for up to two years of missed exemptions. Contact EPCAD to discuss your specific situation and determine whether you are eligible for a refund of{' '}
              <a
                href="/elpaso-tx/owing-too-much-property-tax"
                className="text-teal-600 hover:text-teal-700 font-semibold"
              >
                property taxes you may have overpaid
              </a>
              . The refund process requires filing the exemption application with a note requesting retroactive application, and EPCAD will coordinate with the El Paso County Tax Assessor-Collector to process any refund you are owed.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
